import { api, apiJson } from './client';
import type { EnvName, Host, Service, ServiceKind } from './types';

export type Severity = 'warn' | 'crit';
export type RuleSource = 'metrics' | 'service_states' | 'health_checks';
export type ScopeEnv = EnvName | 'any';

export interface AdminRule {
  id: string;
  severity: Severity;
  source: RuleSource;
  metric: string | null;
  scope_env: ScopeEnv;
  condition: string;
  cooldown: string;
  targets: string[];
}

export type AdminTarget =
  | { id: string; type: 'slack'; webhook_env: string }
  | { id: string; type: 'email'; to: string[]; from: string };

export interface SecretStatus {
  is_set: boolean;
  sensitive: boolean;
  value?: string;
}
export interface SecretsResponse {
  env_file: string;
  exists: boolean;
  status: Record<string, SecretStatus>;
}

export interface ParseConditionOk {
  ok: true;
  condition: unknown;
}
export interface ParseConditionErr {
  ok: false;
  error: string;
}

export const adminApi = {
  // Hosts
  listHosts: () => api<Host[]>('/api/admin/hosts'),
  createHost: (h: Host) => apiJson<Host>('/api/admin/hosts', 'POST', h),
  updateHost: (h: Host) => apiJson<Host>(`/api/admin/hosts/${encodeURIComponent(h.id)}`, 'PUT', h),
  deleteHost: (id: string) => apiJson<void>(`/api/admin/hosts/${encodeURIComponent(id)}`, 'DELETE'),

  // Services
  listServices: () => api<Service[]>('/api/admin/services'),
  createService: (s: ServiceInput) => apiJson<Service>('/api/admin/services', 'POST', s),
  updateService: (s: ServiceInput) =>
    apiJson<Service>(`/api/admin/services/${encodeURIComponent(s.id)}`, 'PUT', s),
  deleteService: (id: string) =>
    apiJson<void>(`/api/admin/services/${encodeURIComponent(id)}`, 'DELETE'),
  restartService: (id: string) =>
    apiJson<{ ok: boolean; nssm_service?: string; error?: string }>(
      `/api/admin/services/${encodeURIComponent(id)}/restart`,
      'POST',
    ),

  // Rules
  listRules: () => api<AdminRule[]>('/api/admin/rules'),
  createRule: (r: AdminRuleInput) =>
    apiJson<{ ok: boolean; id: string }>('/api/admin/rules', 'POST', r),
  updateRule: (r: AdminRuleInput) =>
    apiJson<{ ok: boolean; id: string }>(`/api/admin/rules/${encodeURIComponent(r.id)}`, 'PUT', r),
  deleteRule: (id: string) => apiJson<void>(`/api/admin/rules/${encodeURIComponent(id)}`, 'DELETE'),
  parseCondition: (condition: string) =>
    apiJson<ParseConditionOk | ParseConditionErr>('/api/admin/parse-condition', 'POST', {
      condition,
    }),

  // Targets
  listTargets: () => api<AdminTarget[]>('/api/admin/targets'),
  createTarget: (t: AdminTarget) => apiJson<AdminTarget>('/api/admin/targets', 'POST', t),
  updateTarget: (t: AdminTarget) =>
    apiJson<AdminTarget>(`/api/admin/targets/${encodeURIComponent(t.id)}`, 'PUT', t),
  deleteTarget: (id: string) =>
    apiJson<void>(`/api/admin/targets/${encodeURIComponent(id)}`, 'DELETE'),
  testTarget: (id: string) =>
    apiJson<{ ok: boolean; channel?: string; via?: string; to?: string[]; error?: string }>(
      `/api/admin/targets/${encodeURIComponent(id)}/test`,
      'POST',
    ),

  // Secrets
  secretsStatus: () => api<SecretsResponse>('/api/admin/secrets/status'),
  updateSecrets: (updates: Record<string, string>, restart = true) =>
    apiJson<{ ok: boolean; updated: string[]; will_restart: boolean }>(
      '/api/admin/secrets',
      'POST',
      { updates, restart },
    ),

  // Users
  listUsers: () => api<AdminUser[]>('/api/admin/users'),
  createUser: (u: { email: string; name?: string; role: 'admin' | 'user'; enabled?: boolean }) =>
    apiJson<AdminUser>('/api/admin/users', 'POST', u),
  updateUser: (
    email: string,
    patch: { name?: string | null; role?: 'admin' | 'user'; enabled?: boolean },
  ) => apiJson<AdminUser>(`/api/admin/users/${encodeURIComponent(email)}`, 'PUT', patch),
  deleteUser: (email: string) =>
    apiJson<void>(`/api/admin/users/${encodeURIComponent(email)}`, 'DELETE'),

  // Sessions (access monitoring — local + cross-app)
  listSessions: (
    filters: {
      email?: string;
      status?: 'active' | 'expired' | 'logged_out' | 'all';
      limit?: number;
      app?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (filters.email) qs.set('email', filters.email);
    if (filters.status) qs.set('status', filters.status);
    if (filters.limit) qs.set('limit', String(filters.limit));
    if (filters.app) qs.set('app', filters.app);
    const q = qs.toString();
    return api<AdminSession[]>(`/api/admin/sessions${q ? `?${q}` : ''}`);
  },
  revokeSession: (id: string) =>
    apiJson<void>(`/api/admin/sessions/${encodeURIComponent(id)}/revoke`, 'POST'),
  dedupeActiveSessions: () =>
    apiJson<{ revoked: number }>('/api/admin/sessions/dedupe-active', 'POST'),
  geoStats: () => api<GeoStats>('/api/admin/sessions/geo-stats'),

  // Devices (office laptop / desktop registry)
  listDevices: () => api<AdminDevice[]>('/api/admin/devices'),
  setDeviceEnabled: (id: string, enabled: boolean) =>
    apiJson<void>(`/api/admin/devices/${encodeURIComponent(id)}`, 'PATCH', { enabled }),
  deleteDevice: (id: string) =>
    apiJson<void>(`/api/admin/devices/${encodeURIComponent(id)}`, 'DELETE'),
};

export interface AdminUser {
  email: string;
  name: string | null;
  role: 'admin' | 'user';
  enabled: boolean;
  created_at: string;
  created_by: string | null;
  last_seen_at: string | null;
}

export interface AdminSession {
  id: string;
  email: string;
  app: string;
  ip: string | null;
  geo_country: string | null;
  geo_region: string | null;
  geo_city: string | null;
  geo_lat: number | null;
  geo_lon: number | null;
  geo_asn?: number | null;
  geo_isp?: string | null;
  // Browser-reported HTML5 Geolocation (GPS + WiFi triangulation). When
  // present, prefer these over geo_lat/geo_lon — they're 10-50m accurate
  // on mobile vs ~50km city-level on IP.
  browser_lat?: number | null;
  browser_lng?: number | null;
  browser_accuracy_m?: number | null;
  geo_source?: 'browser' | 'ip' | 'none';
  geo_consent?: 'granted' | 'denied' | 'unsupported' | null;
  geo_captured_at?: string | null;
  connection_type?: 'trusted_office' | 'mobile' | 'hosting' | 'tor' | 'residential' | 'unknown';
  connection_risk?: 'low' | 'medium' | 'high' | 'critical' | 'unknown';
  connection_label?: string;
  browser: string;
  os: string;
  user_agent: string;
  fingerprint: string | null;
  created_at: string;
  last_seen_at: string | null;
  logout_at: string | null;
  expires_at: string;
  duration_sec: number;
  is_active: boolean;
}

export interface GeoStats {
  window_days: number;
  total: number;
  asked: number;
  granted: number;
  denied: number;
  unsupported: number;
  unasked: number;
  consent_rate_pct: number;
  with_browser_geo: number;
  avg_accuracy_m: number | null;
  best_accuracy_m: number | null;
}

export interface AdminDevice {
  id: string;
  hostname: string;
  username: string;
  primary_mac: string | null;
  all_macs: Array<{ Description?: string; MACAddress?: string }>;
  first_seen_at: string;
  last_seen_at: string | null;
  last_ip: string | null;
  last_geo_country: string | null;
  last_geo_city: string | null;
  report_count: number;
  enabled: boolean;
  notes: string | null;
}

export interface ServiceInput {
  id: string;
  host_id: string;
  env: EnvName;
  name: string;
  kind: ServiceKind;
  nssm_service?: string;
  port?: number;
  health_url?: string;
  enabled: boolean;
  accept_redirects: boolean;
}

export interface AdminRuleInput {
  id: string;
  severity: Severity;
  source: RuleSource;
  metric?: string;
  scope_env: ScopeEnv;
  condition: string;
  cooldown: string;
  targets: string[];
}
